import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { scrollToTop } from '../utils/smoothScroll';
import './Navigation.css';

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'EN' },
  { code: 'ka', label: 'GE' },
];

const getLanguageLabel = (code) =>
  LANGUAGE_OPTIONS.find((option) => option.code === code)?.label ||
  code.toUpperCase();

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleLangMenu = () => {
    setIsLangMenuOpen(!isLangMenuOpen);
  };

  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split(
    '-'
  )[0];

  const isHomePage = location.pathname === '/';

  const handleLogoClick = (event) => {
    setIsMenuOpen(false);
    if (!isHomePage) {
      return;
    }
    event.preventDefault();
    scrollToTop();
    if (window.location.hash) {
      window.history.replaceState(null, '', '/');
    }
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <div className="nav-logo">
          <Link to="/" className="logo-link" onClick={handleLogoClick}>
            <span className="logo-text">Veralyon</span>
          </Link>
        </div>

        <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`} id="main-navigation-menu">
          <Link
            to="/about"
            className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.about')}
          </Link>
          <Link
            to="/products"
            className={`nav-link ${location.pathname.startsWith('/products') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.products')}
          </Link>
          <Link
            to="/services"
            className={`nav-link ${location.pathname.startsWith('/services') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.services')}
          </Link>
          <Link
            to="/contact"
            className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.contact')}
          </Link>
        </div>

        <div className="nav-actions">
          <div className="language-switcher">
            <button 
              className="lang-button" 
              onClick={toggleLangMenu}
              aria-label={`${t('nav.changeLanguage')}: ${getLanguageLabel(currentLanguage)}`}
              aria-expanded={isLangMenuOpen}
              aria-controls="language-menu"
            >
              <span className="lang-code">{getLanguageLabel(currentLanguage)}</span>
              <svg 
                className={`lang-arrow ${isLangMenuOpen ? 'open' : ''}`} 
                width="12" 
                height="12" 
                viewBox="0 0 12 12" 
                fill="none"
              >
                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {isLangMenuOpen && (
              <div className="lang-dropdown" id="language-menu" role="menu">
                {LANGUAGE_OPTIONS.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    className={`lang-option ${currentLanguage === code ? 'active' : ''}`}
                    onClick={() => changeLanguage(code)}
                    role="menuitemradio"
                    aria-checked={currentLanguage === code}
                    aria-label={t(`language.${code}`)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button 
            className={`nav-toggle ${isMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label={t('nav.toggleMenu')}
            aria-expanded={isMenuOpen}
            aria-controls="main-navigation-menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;

